/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuthenticationContext } from '@opengeoweb/authentication';
import type { ListFilter } from '@opengeoweb/shared';
import type { CoreAppStore, uiTypes } from '@opengeoweb/store';
import {
  PUBLIC_WARNING_DRAW_TOOL_ID,
  uiActions,
  uiSelectors,
  useSetupDialog,
  useUpdateFeatureSyncLayers,
} from '@opengeoweb/store';
import { areaErrorType, AreaDrawingLoader } from './AreaDrawingLoader';
import { useAreaKeywords, useDrawings } from '../../api/warning-api/hooks';
import type {
  DrawingListItem,
  FetchDrawingParams,
} from '../../store/warningsDrawings/types';
import { areaDrawingLoaderType } from '../../store/warningsDrawings/utils';
import { warningsFormActions } from '../../store/warningsForm/reducer';
import {
  getPublicWarning,
  getPublicWarningAreas,
  getPublicWarningId,
  getPublicWarningStatus,
} from '../../store/warningsForm/selectors';
import { PublicWarningStatus } from '../../store/warningsForm/types';
import { publicWarningDialogType } from '../../store/warningsForm/utils';
import { useWarningsTranslation } from '../../utils/i18n';
import { warningLevelFeatureColors } from '../PublicWarningsForm';
import type { WarningLevel } from '../PublicWarningsForm/LevelField';

const getFilterParams = (
  areas: ListFilter[],
  searchQuery: string,
): FetchDrawingParams => {
  const keywords = areas.reduce<string[]>((areaList, area) => {
    if (area.isSelected) {
      return areaList.concat(area.id);
    }
    return areaList;
  }, []);

  return {
    ...(keywords.length && {
      keywords: keywords.join(','),
    }),
    ...(searchQuery.length && {
      search: searchQuery,
    }),
  };
};

const WARNING_DIALOG_DRAFT_LAYER_ID = 'warning-dialog-draft-area';

const createWarningDialogAreaLayerId = (
  areaId: string | undefined,
  index: number,
): string => areaId ?? `${WARNING_DIALOG_DRAFT_LAYER_ID}-${index}`;

const useAreaRequest = (
  isDialogOpen: boolean,
): {
  areas: ListFilter[];
  fetchAreaError: uiTypes.UISetErrorPayload | undefined;
  isAreasLoading: boolean;
  fetchAreaKeywords: () => void;
  setAreas: (areas: ListFilter[]) => void;
} => {
  const [areas, setAreas] = useState<ListFilter[]>([]);
  const [fetchAreaError, setFetchAreaError] = useState<
    uiTypes.UISetErrorPayload | undefined
  >(undefined);

  const {
    data: areaKeywords,
    isLoading: isAreasLoading,
    error,
    refetch: fetchAreaKeywords,
  } = useAreaKeywords(isDialogOpen);

  useEffect(() => {
    if (areaKeywords) {
      const keyWords = areaKeywords.map((keyword) => ({
        label: keyword === 'Objects' ? 'Drawings' : keyword,
        id: keyword,
        isSelected: false,
        isDisabled: false,
      }));
      setAreas(keyWords);
    }
  }, [areaKeywords]);

  useEffect(() => {
    if (error instanceof Error) {
      setFetchAreaError({
        error: error.message,
        type: areaErrorType as uiTypes.DialogType,
      });
    }
  }, [error]);

  return { areas, fetchAreaError, isAreasLoading, fetchAreaKeywords, setAreas };
};

export const AreaDrawingLoaderConnect: React.FC<{
  bounds?: string;
  source?: uiTypes.Source;
}> = ({ bounds, source = 'app' }) => {
  const { auth } = useAuthenticationContext();
  const { t } = useWarningsTranslation();
  const { dialogOrder, setDialogOrder, isDialogOpen, uiSource, uiError } =
    useSetupDialog(areaDrawingLoaderType, source);

  const dispatch = useDispatch();

  const [items, setItems] = useState<DrawingListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { areas, fetchAreaKeywords, fetchAreaError, setAreas, isAreasLoading } =
    useAreaRequest(isDialogOpen);

  const selectedAreas = useSelector((store: CoreAppStore) =>
    getPublicWarningAreas(store),
  );
  const warningId = useSelector((store: CoreAppStore) =>
    getPublicWarningId(store),
  );

  const warningStatus = useSelector((store: CoreAppStore) =>
    getPublicWarningStatus(store),
  );

  const selectedWarning = useSelector((store: CoreAppStore) =>
    getPublicWarning(store),
  );
  const isWarningDialogOpen = useSelector((store: CoreAppStore) =>
    uiSelectors.getisDialogOpen(store, publicWarningDialogType),
  );
  const hasPublicWarningDrawLayer = useSelector((store: CoreAppStore) =>
    (store.layers?.allIds ?? []).some((layerId) =>
      layerId.startsWith(`drawLayer-${PUBLIC_WARNING_DRAW_TOOL_ID}-`),
    ),
  );

  const isAviationWarning =
    selectedWarning?.warningDetail?.level === 'SIG' ||
    selectedWarning?.warningDetail?.level === 'AIR';

  const warningDialogOriginalId =
    selectedWarning?.id ?? selectedWarning?.warningDetail?.id;
  const warningDialogFeatureLayersById = React.useMemo(() => {
    // Aviation warnings render their active geometry via draw layers/form state.
    // Publishing warning-dialog feature-sync layers for them creates a stale
    // mirror from persisted warningDetail.areas while editing.
    if (
      !isWarningDialogOpen ||
      isAviationWarning ||
      hasPublicWarningDrawLayer
    ) {
      return {};
    }

    return (selectedWarning?.warningDetail?.areas ?? []).reduce<
      Record<
        string,
        {
          id: string;
          geojson: GeoJSON.FeatureCollection;
          originalId?: string;
        }
      >
    >((layersById, area, index) => {
      const styledGeoJSON = warningLevelFeatureColors(
        selectedWarning?.warningDetail?.level as WarningLevel,
        area.geoJSON,
        isAviationWarning,
      );

      if (!styledGeoJSON.features.length) {
        return layersById;
      }

      const layerId = createWarningDialogAreaLayerId(area.areaId, index);
      const geojson = {
        ...styledGeoJSON,
        features: styledGeoJSON.features.map((feature) => ({
          ...feature,
          properties: {
            ...feature.properties,
            originalId:
              warningDialogOriginalId ??
              area.areaId ??
              area.published_warning_uuid,
            warningLevel: selectedWarning?.warningDetail.level,
            hover: true,
          },
        })),
      };

      return {
        ...layersById,
        [layerId]: {
          id: layerId,
          geojson,
          originalId:
            warningDialogOriginalId ??
            area.areaId ??
            area.published_warning_uuid,
        },
      };
    }, {});
  }, [
    isAviationWarning,
    hasPublicWarningDrawLayer,
    isWarningDialogOpen,
    selectedWarning,
    warningDialogOriginalId,
  ]);
  const shouldPublishWarningDialogLayer =
    Object.keys(warningDialogFeatureLayersById).length > 0;

  useUpdateFeatureSyncLayers(
    warningDialogFeatureLayersById,
    shouldPublishWarningDialogLayer ? 'warning-dialog' : undefined,
  );

  const filterParams = React.useMemo(
    () => getFilterParams(areas, searchQuery),
    [areas, searchQuery],
  );

  const {
    data: drawings,
    error,
    refetch,
  } = useDrawings(filterParams, isDialogOpen);

  useEffect(() => {
    if (drawings) {
      setItems(drawings);
    }
  }, [drawings]);

  useEffect(() => {
    if (isDialogOpen && auth) {
      fetchAreaKeywords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDialogOpen, auth]);

  useEffect(() => {
    if (error) {
      setLoading(false);
      dispatch(
        uiActions.setErrorDialog({
          error: error.message,
          type: areaDrawingLoaderType,
        }),
      );
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (isDialogOpen) {
      void refetch();
    }
  }, [filterParams, isDialogOpen, refetch]);

  const onClose = (): void => {
    dispatch(
      uiActions.setActiveMapIdForDialog({
        type: areaDrawingLoaderType,
        mapId: '',
        setOpen: false,
        source,
      }),
    );
  };

  const onClickFilterChip = (id: string, isSelected: boolean): void => {
    const updatedAreas = areas.map((area) => {
      if (id === 'all') {
        return { ...area, isSelected: !isSelected };
      }
      return area.id === id
        ? {
            ...area,
            isSelected,
          }
        : area;
    });

    setAreas(updatedAreas);
  };

  const onSearch = (queryString: string): void => setSearchQuery(queryString);

  const onAddArea = ({ geoJSON, objectName }: DrawingListItem): void => {
    dispatch(
      warningsFormActions.addArea({
        area: { geoJSON, objectName },
      }),
    );
  };

  const onRemoveArea = ({ geoJSON, objectName }: DrawingListItem): void => {
    dispatch(
      warningsFormActions.removeArea({
        area: { geoJSON, objectName },
      }),
    );
  };

  const onRetryRequest = (errorType: string): void => {
    if (errorType === areaErrorType) {
      fetchAreaKeywords();
    }
  };

  const isChangeAreaDisabled =
    warningId !== undefined &&
    typeof warningId === 'string' &&
    warningId.length > 0 &&
    warningStatus !== PublicWarningStatus.DRAFT &&
    warningStatus !== PublicWarningStatus.DRAFT_PUBLISHED;

  const drawingError = uiError
    ? { type: areaDrawingLoaderType, error: uiError }
    : undefined;

  const shouldDisableRemoveWarning =
    warningStatus !== undefined &&
    warningStatus !== PublicWarningStatus.DRAFT &&
    areas.length > 0;

  return (
    <AreaDrawingLoader
      bounds={bounds}
      drawingListItems={items}
      error={drawingError ?? fetchAreaError}
      filters={areas}
      headerSize="xs"
      isChangeAreaDisabled={isChangeAreaDisabled}
      isLoading={loading ?? isAreasLoading}
      isOpen={isDialogOpen}
      onAddArea={onAddArea}
      onClickFilterChip={onClickFilterChip}
      onClose={onClose}
      onMouseDown={setDialogOrder}
      onRemoveArea={onRemoveArea}
      onRetryRequest={onRetryRequest}
      onSearch={onSearch}
      order={dialogOrder}
      searchQuery={searchQuery}
      selectedAreas={selectedAreas}
      shouldDisableRemoveWarning={shouldDisableRemoveWarning}
      source={uiSource}
      startPosition={{ top: 104, left: 50 }}
      title={t('area-drawing-loader-title')}
    />
  );
};
